import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FullPageSkeleton } from '../UI/Skeleton';
import { AuthLayout } from './AuthLayout';
import Login from '../../pages/Auth/Login';
import Register from '../../pages/Auth/Register';
import EmailVerification from '../../pages/Auth/EmailVerification';
import ForgotPassword from '../../pages/Auth/ForgotPassword';
import ResetPassword from '../../pages/Auth/ResetPassword';

const AUTH_PAGES: Record<string, React.ComponentType> = {
  '/login': Login,
  '/register': Register,
  '/verify-email': EmailVerification,
  '/forgot-password': ForgotPassword,
  '/reset-password': ResetPassword,
};

const PUBLIC_AUTH_PATHS = new Set(['/login', '/register', '/forgot-password']);

function AuthPublicGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isBootstrapping, workspaceMode, availableWorkspaces, user } = useAuth();

  if (isBootstrapping) {
    return <FullPageSkeleton />;
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (availableWorkspaces.length > 1) {
    return <Navigate to="/workspace/select" replace />;
  }

  if (workspaceMode === 'ca') {
    return (
      <Navigate
        to={user?.needs_ca_workspace_setup === true ? '/ca/workspace-setup' : '/ca/dashboard'}
        replace
      />
    );
  }

  return <Navigate to="/dashboard" replace />;
}

/**
 * Renders the auth page from the current URL path (not route `children`).
 * Avoids React Router + lazy + startTransition cases where the URL changes but Login stays mounted.
 */
export const AuthEntry: React.FC = () => {
  const { pathname } = useLocation();
  const Page = AUTH_PAGES[pathname];

  if (!Page) {
    return <Navigate to="/login" replace />;
  }

  const content = (
    <AuthLayout key={pathname}>
      <Page key={pathname} />
    </AuthLayout>
  );

  if (PUBLIC_AUTH_PATHS.has(pathname)) {
    return <AuthPublicGate>{content}</AuthPublicGate>;
  }

  return content;
};

export const AUTH_ROUTE_PATHS = Object.keys(AUTH_PAGES);
